(function () {
  const root = document.querySelector('[data-nouveaupack-root]');
  if (!root) return;

  const errorBox = root.querySelector('[data-nouveaupack-error]');
  const addBtn = root.querySelector('[data-nouveaupack-add]');

  const norm = (v) => (v == null ? null : String(v).trim());

  function showError(msg) {
    if (!errorBox) return;
    errorBox.textContent = msg || '';
    errorBox.hidden = !msg;
  }

  // ---------- Shopify product hydrate ----------
  const productCache = new Map(); // handle -> product json

  async function fetchProductByHandle(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) return null;
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (e) {
      return null;
    }
  }

  function parseEmbeddedProduct(wrapper) {
    try {
      return JSON.parse(wrapper.dataset.product);
    } catch (e) {
      return null;
    }
  }

  function getOptionsCount(product) {
    if (Array.isArray(product?.options)) return product.options.length;
    // fallback
    const v = product?.variants?.[0];
    if (!v) return 0;
    if (Array.isArray(v.options)) return v.options.length;
    let c = 0;
    if (v.option1 != null) c++;
    if (v.option2 != null) c++;
    if (v.option3 != null) c++;
    return c;
  }

  function getVariantOption(v, i) {
    if (!v) return null;
    if (Array.isArray(v.options)) return norm(v.options[i]);
    if (i === 0) return norm(v.option1);
    if (i === 1) return norm(v.option2);
    if (i === 2) return norm(v.option3);
    return null;
  }

  function variantIsPurchasable(v) {
    if (!v || !v.id) return false;

    // sur /products/handle.js, available est généralement présent
    if (typeof v.available === 'boolean') return v.available;

    // fallback
    if (v.inventory_policy === 'continue') return true;
    if (typeof v.inventory_quantity === 'number' && v.inventory_policy === 'deny') {
      return v.inventory_quantity > 0;
    }

    // inconnu => ne pas bloquer
    return true;
  }

  // ---------- selection helpers ----------
  function getSelectedPartial(wrapper, product) {
    const n = getOptionsCount(product);
    const selected = new Array(n).fill(null);

    wrapper.querySelectorAll('[data-nouveaupack-option]').forEach(sel => {
      selected[Number(sel.dataset.optionIndex)] = norm(sel.value);
    });

    wrapper.querySelectorAll('[data-nouveaupack-option-button]').forEach(inp => {
      selected[Number(inp.dataset.optionIndex)] = norm(inp.value);
    });

    wrapper.querySelectorAll('[data-nouveaupack-option-fixed]').forEach(inp => {
      selected[Number(inp.dataset.optionIndex)] = norm(inp.value);
    });

    return selected;
  }

  function setActiveButtons(wrapper, optionIndex, value) {
    const val = norm(value);

    const hidden = wrapper.querySelector(
      `[data-nouveaupack-option-button][data-option-index="${optionIndex}"]`
    );
    if (hidden) hidden.value = val || '';

    wrapper.querySelectorAll(
      `[data-nouveaupack-swatch][data-option-index="${optionIndex}"]`
    ).forEach(b => {
      const active = norm(b.dataset.value) === val;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function findExactVariant(product, selected) {
    const vars = product?.variants || [];
    if (!vars.length) return null;

    // si des options sont null -> on ne cherche pas exact
    if (selected.some(v => v == null)) return null;

    return vars.find(v => {
      for (let i = 0; i < selected.length; i++) {
        if (getVariantOption(v, i) !== selected[i]) return false;
      }
      return true;
    }) || null;
  }

  // renvoie une variante “compatible” (même si pas exact) parmi celles achetables
  function findFirstCompatiblePurchasableVariant(product, selectedPartial) {
    const vars = product?.variants || [];
    const purchasables = vars.filter(variantIsPurchasable);

    // 1) priorité aux variants qui matchent les choix déjà fixés
    const best = purchasables.find(v => {
      for (let i = 0; i < selectedPartial.length; i++) {
        const sel = selectedPartial[i];
        if (sel != null && getVariantOption(v, i) !== sel) return false;
      }
      return true;
    });

    return best || purchasables[0] || null;
  }

  function isChoiceAvailable(product, selectedPartial, optionIndex, value) {
    const testVal = norm(value);
    if (!testVal) return false;

    return (product?.variants || []).some(v => {
      if (!variantIsPurchasable(v)) return false;
      if (getVariantOption(v, optionIndex) !== testVal) return false;

      for (let i = 0; i < selectedPartial.length; i++) {
        if (i === optionIndex) continue;
        const sel = selectedPartial[i];
        if (sel != null && getVariantOption(v, i) !== sel) return false;
      }
      return true;
    });
  }

  // ---------- update per item ----------
  async function ensureHydrated(wrapper) {
    // on hydrate via data-handle (fiable)
    const handle = wrapper.dataset.handle;
    const hydrated = await fetchProductByHandle(handle);

    // si la fetch marche, on remplace le JSON embarqué
    if (hydrated && hydrated.variants && hydrated.variants.length) {
      wrapper.dataset.product = JSON.stringify(hydrated);
      return hydrated;
    }

    // fallback : JSON embarqué
    return parseEmbeddedProduct(wrapper);
  }

  async function updateItem(item) {
    const wrapper = item.querySelector('[data-nouveaupack-product]');
    if (!wrapper) return;

    const product = await ensureHydrated(wrapper);
    if (!product || !product.variants || !product.variants.length) {
      // si on n’a pas de variants, on ne peut pas fonctionner
      const variantInput = wrapper.querySelector('[data-nouveaupack-variant]');
      if (variantInput) variantInput.value = '';
      showError('problème de variantes produit');
      return;
    }

    const selectedPartial = getSelectedPartial(wrapper, product);

    // 1) griser tailles (si présentes)
    const sizeHidden = wrapper.querySelector('[data-nouveaupack-option-button]');
    if (sizeHidden) {
      const idx = Number(sizeHidden.dataset.optionIndex);
      const buttons = Array.from(
        wrapper.querySelectorAll(`[data-nouveaupack-swatch][data-option-index="${idx}"]`)
      );

      buttons.forEach(btn => {
        const ok = isChoiceAvailable(product, selectedPartial, idx, btn.dataset.value);
        btn.disabled = !ok;
        btn.classList.toggle('is-disabled', !ok);
        btn.setAttribute('aria-disabled', !ok ? 'true' : 'false');
      });

      // si la taille courante est devenue indispo, bascule sur la première dispo
      const current = norm(sizeHidden.value);
      const currentOk = current ? isChoiceAvailable(product, selectedPartial, idx, current) : false;

      if (!currentOk) {
        const firstOk = buttons.find(b => !b.disabled);
        if (firstOk) {
          setActiveButtons(wrapper, idx, firstOk.dataset.value);
          selectedPartial[idx] = norm(firstOk.dataset.value);
        }
      }
    }

    // 2) griser options des selects (si présents)
    wrapper.querySelectorAll('[data-nouveaupack-option]').forEach(sel => {
      const idx = Number(sel.dataset.optionIndex);
      const opts = Array.from(sel.options);

      opts.forEach(opt => {
        opt.disabled = !isChoiceAvailable(product, selectedPartial, idx, opt.value);
      });

      const current = norm(sel.value);
      const currentOpt = opts.find(o => norm(o.value) === current);
      if (currentOpt && currentOpt.disabled) {
        const firstOk = opts.find(o => !o.disabled);
        if (firstOk) sel.value = firstOk.value;
      }
    });

    // 3) trouver variant à ajouter
    const variantInput = wrapper.querySelector('[data-nouveaupack-variant]');
    const exact = findExactVariant(product, getSelectedPartial(wrapper, product)); // après éventuels auto-ajustements

    let chosen = exact && variantIsPurchasable(exact) ? exact : null;

    if (!chosen) {
      chosen = findFirstCompatiblePurchasableVariant(product, getSelectedPartial(wrapper, product));
    }

    if (!chosen) {
      if (variantInput) variantInput.value = '';
      showError('une variante est indisponible');
      return;
    }

    if (variantInput) variantInput.value = chosen.id;
    showError('');
  }

  async function refreshAll() {
    const items = Array.from(root.querySelectorAll('[data-nouveaupack-item]'));
    for (const it of items) {
      // eslint-disable-next-line no-await-in-loop
      await updateItem(it);
    }
  }

  // ---------- init ----------
  (async function init() {
    // bind selects
    root.querySelectorAll('[data-nouveaupack-item]').forEach(item => {
      item.querySelectorAll('[data-nouveaupack-option]').forEach(select => {
        select.addEventListener('change', () => updateItem(item));
      });
    });

    await refreshAll();
  })();

  // clic tailles
  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-nouveaupack-swatch]');
    if (!btn) return;
    if (!root.contains(btn)) return;
    if (btn.disabled || btn.classList.contains('is-disabled')) return;

    const wrapper = btn.closest('[data-nouveaupack-product]');
    if (!wrapper) return;

    const idx = Number(btn.dataset.optionIndex);
    setActiveButtons(wrapper, idx, btn.dataset.value);

    const item = wrapper.closest('[data-nouveaupack-item]');
    if (item) updateItem(item);
  });

  // ajout panier
  addBtn.addEventListener('click', async () => {
    showError('');
    addBtn.disabled = true;

    try {
      await refreshAll();

      const items = [];
      root.querySelectorAll('[data-nouveaupack-product]').forEach(w => {
        const variantId = w.querySelector('[data-nouveaupack-variant]')?.value;
        const qty = parseInt(w.dataset.qty, 10) || 1;

        if (!variantId) throw new Error('sélection invalide');
        items.push({ id: Number(variantId), quantity: qty });
      });

      if (!items.length) throw new Error('aucun article à ajouter');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (_) {}
        throw new Error((data && (data.description || data.message)) || 'erreur ajout panier');
      }

      window.location.href = '/cart';
    } catch (e) {
      showError(e && e.message ? e.message : 'impossible d’ajouter le pack au panier');
    } finally {
      addBtn.disabled = false;
    }
  });
})();
